/**
 * Controle de faturamento das assinaturas (out/2026): tela admin
 * /admin/faturamento (GET/PATCH /billing…) e o endereço de faturamento do
 * próprio cliente em /conta (GET/PATCH /users/billing-info). Os gates de admin
 * e de dono da conta são do backend — aqui só se chama.
 */
import api, { ApiResponse } from './api';

export type BillingCycle = 'monthly' | 'quarterly' | 'yearly';
export type BillingChargeStatus =
  | 'nf_requested'
  | 'nf_issued'
  | 'paid'
  | 'canceled';
export type AgingBucket = '0-30' | '31-60' | '60+';

export interface BillingInfo {
  billingAddress: string | null;
  billingZipCode: string | null;
  billingCity: string | null;
  billingState: string | null;
}

export interface BillingSubscription extends BillingInfo {
  subscriptionId: string;
  userId: string;
  status: 'active' | 'trial' | 'past_due' | 'suspended' | 'canceled';
  contractedAt: string;
  planName: string;
  planPrice: number;
  groupACount: number;
  groupBCount: number;
  extraGroupA: number;
  extraGroupB: number;
  billingAnchorDate: string | null;
  billingCycle: BillingCycle;
  negotiatedAmount: number | null;
  paymentDueDays: number;
  paymentMethod: string | null;
  pendingAdjustment: number;
  pendingAdjustmentNote: string | null;
  // Gratuidade (teste/benefício): plano pago sem cobrança até complimentaryUntil
  isComplimentary: boolean;
  complimentaryUntil: string | null;
  complimentaryReason: string | null;
  name: string;
  email: string;
  isCompany: boolean;
  companyName: string | null;
  documentNumber: string | null;
  phone: string | null;
  invoiceEmail: string | null;
  estimatedMonthly: number;
  cycleAmount: number;
  monthlyEquivalent: number;
  nextBillingDate: string | null; // data da próxima cobrança (postecipada)
  nextPeriodStart: string | null; // início do período que ela cobra
  missingFields: string[];
  billable: boolean;
  complimentaryActive: boolean;
  // UCs excluídas no período que a próxima cobrança cobra (seguem cobradas nele)
  deletedUnitsInPeriod?: number;
}

export interface BillingCharge {
  id: string;
  subscriptionId: string;
  userId: string;
  userName: string;
  planName: string;
  periodStart: string;
  periodEnd: string;
  amount: number;
  description: string;
  status: BillingChargeStatus;
  dueDate: string;
  nfRequestedAt: string | null;
  nfRequestCount: number;
  nfNumber: string | null;
  nfIssuedAt: string | null;
  paidAt: string | null;
  paymentMethod: string | null;
  notes: string | null;
  customerSnapshot: {
    name: string | null;
    document: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
  } | null;
  createdBy: string;
  overdue: boolean;
  agingBucket: AgingBucket | null;
}

// Conta de plano pago fora do faturamento (admin ou sem assinatura)
export interface ExcludedBillingAccount {
  userId: string;
  name: string;
  email: string;
  planName: string;
  reason: 'admin' | 'no_subscription';
}

export interface BillingOverview {
  today: string;
  summary: {
    mrr: number;
    forecastMonth: number;
    receivable: number;
    receivedMonth: number;
    overdueTotal: number;
    overdueByBucket: Record<AgingBucket, number>;
    canceledThisMonth: number;
    missingDataCount: number;
    pendingAnchorCount: number;
    complimentaryCount: number;
  };
  subscriptions: BillingSubscription[];
  charges: BillingCharge[];
  excludedAccounts: ExcludedBillingAccount[];
}

export interface BillingConfigPatch {
  billingAnchorDate?: string | null;
  billingCycle?: BillingCycle;
  negotiatedAmount?: number | null;
  paymentDueDays?: number;
  paymentMethod?: string | null;
  pendingAdjustment?: number;
  pendingAdjustmentNote?: string | null;
  isComplimentary?: boolean;
  complimentaryUntil?: string | null;
  complimentaryReason?: string | null;
}

export interface BillingChargePatch {
  status?: BillingChargeStatus;
  amount?: number;
  description?: string;
  dueDate?: string;
  nfNumber?: string | null;
  paidAt?: string | null;
  paymentMethod?: string | null;
  notes?: string | null;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

export const getBillingOverviewService = (): ApiResponse<
  Envelope<BillingOverview>
> => api({ method: 'GET', url: 'billing', useToken: true });

export const updateSubscriptionBillingService = (
  subscriptionId: string,
  data: BillingConfigPatch
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'PATCH',
    url: `billing/subscriptions/${subscriptionId}`,
    data,
    useToken: true,
  });

export const chargeNowService = (
  subscriptionId: string
): ApiResponse<Envelope<BillingCharge>> =>
  api({
    method: 'POST',
    url: `billing/subscriptions/${subscriptionId}/charges`,
    useToken: true,
  });

export const updateChargeService = (
  chargeId: string,
  data: BillingChargePatch
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'PATCH',
    url: `billing/charges/${chargeId}`,
    data,
    useToken: true,
  });

export const resendNfRequestService = (
  chargeId: string
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'POST',
    url: `billing/charges/${chargeId}/nf-request`,
    useToken: true,
  });

export const updateCustomerBillingInfoService = (
  userId: string,
  data: BillingInfo
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'PATCH',
    url: `billing/customers/${userId}`,
    data,
    useToken: true,
  });

export const getOwnBillingInfoService = (): ApiResponse<
  Envelope<BillingInfo>
> => api({ method: 'GET', url: 'users/billing-info', useToken: true });

export const updateOwnBillingInfoService = (
  data: BillingInfo
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'PATCH',
    url: 'users/billing-info',
    data,
    useToken: true,
  });

// Aviso antes de excluir uma UC (out/2026): a UC segue cobrada no ciclo em que
// esteve ativa — espelho de @shared/utils/unitBilling no backend.
export interface UnitDeletionImpact {
  reason:
    | 'charged_in_cycle'
    | 'included_in_plan'
    | 'negotiated'
    | 'not_billed';
  unitCost: number;
  periodStart: string | null;
  periodEnd: string | null;
  billingDate: string | null;
}

export const getUnitDeletionImpactService = (
  consumerUnitId: string
): ApiResponse<Envelope<UnitDeletionImpact>> =>
  api({
    method: 'GET',
    url: `consumer-units/${consumerUnitId}/deletion-impact`,
    useToken: true,
  });

// Painel de uso das contas (admin, out/2026) — espelho de
// @shared/utils/accountUsageSignals no backend
export type UsageFlag =
  | 'unit_churn'
  | 'short_lived_units'
  | 'analyze_then_delete'
  | 'many_holders'
  | 'free_rotation';

export interface AccountUsageSignal {
  userId: string;
  name: string;
  email: string;
  planName: string;
  accountCreatedAt: string;
  activeUnits: number;
  createdLast90: number;
  deletedLast90: number;
  deletedLast30: number;
  avgLifetimeDaysDeleted: number | null;
  invoicesOnDeletedUnits: number;
  distinctHolders: number;
  distinctInstallations: number;
  flags: UsageFlag[];
}

export interface AccountUsageOverview {
  windowDays: number;
  thresholds: {
    churnMinDeleted: number;
    churnShare: number;
    shortLivedMinDeleted: number;
    shortLivedMaxDays: number;
    analyzeThenDeleteMinInvoices: number;
    manyHoldersMin: number;
    freeRotationMinInstallations: number;
  };
  accounts: AccountUsageSignal[];
}

export const getAccountUsageService = (): ApiResponse<
  Envelope<AccountUsageOverview>
> => api({ method: 'GET', url: 'billing/usage-signals', useToken: true });
